import { NSCheckbox } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { BudgetClassificationTypeEnum, NON_DEFAULT_BUDGET_TYPES } from 'views/ProFormaTable/constants';
import { IBudgetClassificationPatchData, IProFormaUses } from 'views/ProFormaTable/types';
import { useUsesTableContext } from '../../../../context/UsesTableProvider';
import './UsesTableBudgetLineNameCell.scss';

export interface IUsesTableBudgetLineNameCell {
    budgetClassification: IProFormaUses;
    updateBudgetClassification: (updatedValues: Partial<IBudgetClassificationPatchData>) => void;
    isPreview?: boolean;
}

const UsesTableBudgetLineNameCell = ({ budgetClassification, updateBudgetClassification, isPreview }: IUsesTableBudgetLineNameCell) => {
    const { selectedCategories, updateSelectedCategories, isProFormaReadOnly } = useUsesTableContext();

    const isInterestReserve = budgetClassification.classificationType === BudgetClassificationTypeEnum.INTEREST_RESERVE;
    const isOperatingReserve = budgetClassification.classificationType === BudgetClassificationTypeEnum.OPERATING_RESERVE;
    const isFinancingFee = budgetClassification.classificationType === BudgetClassificationTypeEnum.FINANCING_FEE;
    const isUneditableBudgetClassification = isInterestReserve || isOperatingReserve;

    return (
        <div className="d-flex align-items-center h-100">
            <RenderIf isTrue={!isUneditableBudgetClassification && !isProFormaReadOnly && !isPreview}>
                <NSCheckbox
                    id={`selectSubcategory-${budgetClassification.id}`}
                    checked={selectedCategories.budgetClassifications.includes(budgetClassification.id)}
                    callback={() => updateSelectedCategories(null, null, [budgetClassification.id])}
                    containerClassName="custom-control-inline align-middle"
                />
            </RenderIf>
            <div className="d-flex flex-column">
                <NSCellInput
                    value={budgetClassification.budgetLineNickname}
                    handleUpdateValue={updatedValue => {
                        updateBudgetClassification({ budgetLineNickname: String(updatedValue) });
                    }}
                    placeholder="Enter budget line name"
                    customClassName={classNames({
                        'ml-5 pl-1 pr-2': isUneditableBudgetClassification && !isPreview && !isProFormaReadOnly,
                        'ml-4': !isUneditableBudgetClassification || isPreview || (isUneditableBudgetClassification && isProFormaReadOnly),
                        'text-dark': NON_DEFAULT_BUDGET_TYPES.includes(budgetClassification.classificationType),
                        'mt-1': isFinancingFee || isInterestReserve,
                    })}
                    disabled={budgetClassification.classificationType !== BudgetClassificationTypeEnum.DEFAULT}
                />

                <RenderIf isTrue={isFinancingFee}>
                    <div className="UsesTableBudgetLineNameCell__debt-amount d-flex rounded ml-4 pl-1 pr-2">
                        <div className="text-nowrap my-auto mr-1 py-2">% of debt amount: </div>
                        <NSCellInput
                            type="number"
                            numberType="percentage"
                            value={budgetClassification.percentageOfDebt}
                            handleUpdateValue={updatedValue => {
                                updateBudgetClassification({
                                    percentageOfDebt: String(updatedValue),
                                });
                            }}
                            placeholder="Enter percentage"
                            isRightAligned
                            customClassName="my-auto"
                            disabled={isPreview}
                            inputHeight="26px"
                        />
                    </div>
                </RenderIf>

                <RenderIf isTrue={isInterestReserve}>
                    <div className={classNames('UsesTableBudgetLineNameCell__lease-up-income d-flex rounded pr-2', {
                        'ml-5 pl-2': !isPreview && !isProFormaReadOnly,
                        'ml-4 pl-1': isPreview || isProFormaReadOnly,
                    })}
                    >
                        <div className="text-nowrap my-auto mr-1 py-2">Lease-up income: </div>
                        <NSCellInput
                            type="number"
                            numberType="percentage"
                            value={budgetClassification.leaseUpIncomePercentage}
                            handleUpdateValue={updatedValue => {
                                updateBudgetClassification({
                                    leaseUpIncomePercentage: String(updatedValue),
                                });
                            }}
                            placeholder="Enter percentage"
                            isRightAligned
                            customClassName="my-auto"
                            disabled={isPreview}
                            inputHeight="26px"
                        />
                    </div>
                </RenderIf>
            </div>
        </div>
    );
};

export default UsesTableBudgetLineNameCell;
